/**
 * MatomoTracker - Module centralisé pour le tracking Matomo
 * 
 * Ce module gère l'envoi d'événements personnalisés à Matomo
 * avec validation des données et prévention des doublons.
 */

/** IDs des Custom Dimensions Action dans Matomo (Administration > Custom Dimensions) */
const CUSTOM_DIMENSION_IDS = {
    1: 'cta_role',
    2: 'page_type',
    3: 'offre',
    4: 'target_audience',
    5: 'capability'
};

class MatomoTracker {
    constructor() {
        this.isMatomoLoaded = false;
        this.debugMode = true; // Mode debug activé par défaut pour les tests
        this.init();
    }

    /**
     * Initialise le tracker et vérifie la présence de Matomo
     */
    init() {
        // Vérifier si Matomo est déjà chargé
        if (typeof window._paq !== 'undefined') {
            this.isMatomoLoaded = true;
        } else {
            // Attendre que Matomo soit chargé
            this.waitForMatomo();
        }
    }

    /**
     * Attend que Matomo soit chargé (avec timeout)
     */
    waitForMatomo() {
        let attempts = 0;
        const maxAttempts = 50; // 5 secondes max (50 * 100ms)

        const checkInterval = setInterval(() => {
            if (typeof window._paq !== 'undefined') {
                this.isMatomoLoaded = true;
                clearInterval(checkInterval);
            } else {
                attempts++;
                if (attempts >= maxAttempts) {
                    console.warn('MatomoTracker: Matomo (_paq) non détecté après 5 secondes');
                    clearInterval(checkInterval);
                }
            }
        }, 100);
    }

    /**
     * Valide les données de tracking avant envoi
     * @param {Object} data - Les données à valider
     * @returns {boolean} - True si les données sont valides
     */
    validateData(data) {
        const requiredFields = ['cta_type', 'component_name', 'cta_role', 'page_type', 'offre'];
        
        for (const field of requiredFields) {
            if (!data[field] || data[field].trim() === '') {
                console.warn(`MatomoTracker: Champ requis manquant: ${field}`, data);
                return false;
            }
        }

        // Valeurs prédéfinies (suggestions) ; les valeurs personnalisées ACF sont acceptées
        const validCtaTypes = ['démo', 'contact', 'contenu', 'découverte'];
        const validComponents = ['page', 'navigation', 'header', 'content-block', 'feature-flow', 'form', 'product-push', 'tabs', 'onglet', 'popin', 'banner', 'module-cards'];
        const validRoles = ['conversion', 'parcours'];
        const validPageTypes = ['blog', 'solution', 'marché', 'institutionnel'];
        const validOffres = ['paie', 'RH', 'mutuelle_prevoyance', 'partenaire'];
        const validTargetAudience = ['entreprises', 'partenaires', 'mixte'];
        const validCapabilities = ['Paie', 'Dsn', 'IA', 'BI', 'Legal', 'Absences_conges', 'Entretiens', 'Notes_frais', 'Performance_RH', 'Formation', 'Support'];

        const isAllowed = (value, list) => list.includes(value) || (value && value.trim().length > 0);
        if (!isAllowed(data.cta_type, validCtaTypes)) {
            console.warn(`MatomoTracker: cta_type invalide: ${data.cta_type}`);
            return false;
        }
        if (!isAllowed(data.component_name, validComponents)) {
            console.warn(`MatomoTracker: component_name invalide: ${data.component_name}`);
            return false;
        }
        if (!isAllowed(data.cta_role, validRoles)) {
            console.warn(`MatomoTracker: cta_role invalide: ${data.cta_role}`);
            return false;
        }
        if (!isAllowed(data.page_type, validPageTypes)) {
            console.warn(`MatomoTracker: page_type invalide: ${data.page_type}`);
            return false;
        }
        if (!isAllowed(data.offre, validOffres)) {
            console.warn(`MatomoTracker: offre invalide: ${data.offre}`);
            return false;
        }
        if (data.target_audience && !isAllowed(data.target_audience, validTargetAudience)) {
            console.warn(`MatomoTracker: target_audience invalide: ${data.target_audience}`);
            return false;
        }
        if (data.capability && !isAllowed(data.capability, validCapabilities)) {
            console.warn(`MatomoTracker: capability invalide: ${data.capability}`);
            return false;
        }

        return true;
    }

    /**
     * Vérifie si l'événement est un doublon
     * @deprecated Cette méthode a été désactivée pour permettre de tracker chaque clic
     * @param {Object} data - Les données de l'événement
     * @returns {boolean} - Toujours false maintenant (pas de détection de doublons)
     */
    isDuplicate(data) {
        // Désactivé : chaque clic est maintenant tracké, même sur le même élément
        return false;
    }

    /**
     * Formate les données pour Matomo
     * @param {Object} data - Les données brutes
     * @returns {Object} - Les données formatées
     */
    formatData(data) {
        const formatted = {
            cta_type: data.cta_type,
            component_name: data.component_name,
            cta_role: data.cta_role,
            page_type: data.page_type,
            page_url: data.page_url || window.location.href,
            offre: data.offre
        };
        if (data.target_audience) formatted.target_audience = data.target_audience;
        if (data.capability) formatted.capability = data.capability;
        return formatted;
    }

    /**
     * Envoie un événement à Matomo
     * @param {Object} data - Les données de l'événement
     * @param {string} eventName - Le nom de l'événement (optionnel, défaut: 'CTA Click')
     * @param {HTMLElement} element - L'élément DOM (optionnel, pour le debug)
     */
    trackEvent(data, eventName = 'CTA Click', element = null) {
        // Vérifier que Matomo est chargé
        if (!this.isMatomoLoaded && typeof window._paq === 'undefined') {
            console.warn('MatomoTracker: Matomo non chargé, événement non envoyé', data);
            return;
        }

        // Ajouter page_url si manquant
        if (!data.page_url) {
            data.page_url = window.location.href;
        }

        // Formater les données
        const formattedData = this.formatData(data);

        // Valider les données
        if (!this.validateData(formattedData)) {
            return;
        }

        // Note: La détection de doublons a été désactivée pour permettre
        // de tracker chaque clic, même sur le même élément

        // Envoyer à Matomo avec les Custom Dimensions (scope Action) puis trackEvent
        try {
            if (this.debugMode) {
                console.group('🎯 MatomoTracker: Événement détecté');
                console.log('📊 Données:', formattedData);
                console.log('🏷️  Nom événement:', eventName);
                if (element) {
                    console.log('📍 Élément:', element);
                    console.log('🔗 URL:', element.href || element.getAttribute('href') || 'N/A');
                } else {
                    console.log('📍 Type: Formulaire');
                }
            }

            if (typeof window._paq !== 'undefined') {
                const debugCommands = [];

                for (const [id, key] of Object.entries(CUSTOM_DIMENSION_IDS)) {
                    const value = formattedData[key];
                    if (value != null && String(value).trim() !== '') {
                        const dimensionId = parseInt(id, 10);
                        window._paq.push(['setCustomDimension', dimensionId, value]);
                        if (this.debugMode) debugCommands.push(['setCustomDimension', dimensionId, value]);
                    }
                }

                window._paq.push(['trackEvent', eventName, formattedData.component_name, formattedData.cta_type]);
                if (this.debugMode) debugCommands.push(['trackEvent', eventName, formattedData.component_name, formattedData.cta_type]);

                if (this.debugMode) {
                    console.log('✅ Événement envoyé à Matomo via _paq');
                    console.log('📡 Commandes _paq:', debugCommands);
                }
            } else {
                if (this.debugMode) {
                    console.warn('⚠️  Matomo (_paq) non disponible - événement simulé');
                    console.log('💡 Pour tester sans Matomo, les données sont:', formattedData);
                }
            }

            if (this.debugMode) {
                console.groupEnd();
            }
        } catch (error) {
            console.error('❌ MatomoTracker: Erreur lors de l\'envoi à Matomo', error, formattedData);
        }
    }

    /**
     * Extrait les données depuis les attributs data-matomo-* d'un élément
     * @param {HTMLElement} element - L'élément DOM
     * @returns {Object|null} - Les données extraites ou null si incomplètes
     */
    extractDataFromElement(element) {
        const data = {
            cta_type: element.getAttribute('data-matomo-cta-type'),
            component_name: element.getAttribute('data-matomo-component'),
            cta_role: element.getAttribute('data-matomo-role'),
            page_type: element.getAttribute('data-matomo-page-type'),
            offre: element.getAttribute('data-matomo-offre')
        };
        const targetAudience = element.getAttribute('data-matomo-target-audience');
        const capability = element.getAttribute('data-matomo-capability');
        if (targetAudience) data.target_audience = targetAudience;
        if (capability) data.capability = capability;

        // Vérifier que tous les attributs requis sont présents
        const required = ['cta_type', 'component_name', 'cta_role', 'page_type', 'offre'];
        const hasRequired = required.every(key => data[key] !== null && data[key] !== '');

        if (!hasRequired) {
            console.warn('MatomoTracker: Attributs data-matomo-* requis manquants sur l\'élément', element, data);
            return null;
        }

        return data;
    }
}

// Export singleton
window.MatomoTracker = new MatomoTracker;
export default window.MatomoTracker;
// export default new MatomoTracker();

